<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { ref } from 'vue';

import { flattenObject } from '@/utils/InputProcessingUtilities';
import SecondarySideBarMenuVListItem from '@/components/items/SecondarySideBarMenuVListItem.vue';
import { useAuthStore } from '@/stores/useAuthStore';
import { showErrorNotification } from '@/helpers/notifiers';
import { brandConfig } from '@/brand';

export interface Props {
  job?: ReadonlyDeep<
    Pick<
      openapi.JobDetailedOpenapi,
      'current_driver_code' | 'rockhopper_date' | 'rockhopper_id'
    >
  >;
  openNewTicketDialog?: () => void;
  openNewQueryDialog?: () => void;
  openSplitToBaseDialog?: () => void;
  closeMenu?: () => void;
  resync?: () => Promise<void>;
}
const props = withDefaults(defineProps<Props>(), {
  job: undefined,
  openNewTicketDialog: undefined,
  openNewQueryDialog: undefined,
  openSplitToBaseDialog: undefined,
  closeMenu: undefined,
  resync: undefined,
});

const authStore = useAuthStore();

function handleUploadClick(): void {
  const attachmentsEl = document.getElementById('attachments');
  if (!attachmentsEl) return;
  attachmentsEl.scrollIntoView({
    behavior: 'smooth',
  });
  if (props.closeMenu) props.closeMenu();
  setTimeout(() => {
    attachmentsEl.classList.add('g-focus-animation');
    setTimeout(() => {
      attachmentsEl.classList.remove('g-focus-animation');
    }, 2000);
  }, 100);
}

function handlePrintPDFClick(): void {
  if (props.closeMenu) props.closeMenu();
  setTimeout(() => {
    window.print();
  }, 500);
}

function handleDownloadCSVClick(): void {
  if (!props.job) return;
  try {
    const flattenJob = flattenObject(props.job);
    const headers: string[] = [];
    Object.keys(flattenJob).forEach((header) => {
      headers.push(`"${header}"`);
    });
    const data: string[] = [];
    Object.values(flattenJob).forEach((item) => {
      // TODO

      data.push(`"${item}"`);
    });
    const csv = `${headers.toString()}
${data.toString()}`;
    const blob = new Blob([csv], { type: 'csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${props.job.rockhopper_id}_${props.job.rockhopper_date}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    if (props.closeMenu) props.closeMenu();
  } catch (err) {
    showErrorNotification({ title: 'Error downloading the CSV' });
  }
}

const isFetching = ref(false);

async function handleResyncClick(): Promise<void> {
  if (!props.job) return;
  if (!props.resync) return;
  if (isFetching.value) return;
  isFetching.value = true;
  await props
    .resync()
    .then(() => {
      if (props.closeMenu) props.closeMenu();
    })
    .finally(() => {
      isFetching.value = false;
    });
}
</script>

<template>
  <v-list
    :class="$style.detailedJobFooterBtnMenuVList"
    data-testid="detailedJobFooterBtnMenuVList"
  >
    <SecondarySideBarMenuVListItem
      v-if="authStore.isStaff && brandConfig.brand === 'kk'"
      :disabled="isFetching"
      @click="handleResyncClick"
    >
      <template #prepend>
        <v-icon
          v-if="isFetching"
          class="animate-spin"
          icon="mdi-loading"
        ></v-icon>
        <v-icon v-else icon="mdi-refresh"></v-icon>
      </template>
      Re-Sync from RH
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-pencil-outline"
      :disabled="!openNewTicketDialog"
      @click="openNewTicketDialog"
    >
      Create a ticket
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-send-variant-outline"
      :disabled="!openNewQueryDialog"
      @click="openNewQueryDialog"
    >
      Send a query
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-call-split"
      :disabled="!openSplitToBaseDialog"
      @click="openSplitToBaseDialog"
    >
      Split to base
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-upload-outline"
      @click="handleUploadClick"
    >
      Upload a file
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-file-pdf-box"
      @click="handlePrintPDFClick"
    >
      Print PDF
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-file-delimited-outline"
      @click="handleDownloadCSVClick"
    >
      Download CSV
    </SecondarySideBarMenuVListItem>
  </v-list>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobFooterBtnMenuVList {
  font-size: 1rem;
  line-height: 24px;
  padding: 8px 0px !important;
}
</style>
